import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
  Button,
  Rating,
  Skeleton,
  Tooltip,
  TextField,
  IconButton,
} from "@mui/material";
import { Reply, Send } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { useState } from "react";

// Generic feedback list component
const FeedbackList = ({
  feedbacks = [],
  getTypeColor = () => "default",
  onReply,
  handleFeedbackPopup,
  feedbackType
}) => {
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);

   const handleReplySubmit = (feedback) => {
    if (replyText.trim()) {
      onReply?.(feedback, replyText);
      setReplyingTo(null);
      setReplyText("");
    }
  };

  function formatDate(date) {
    if (!date) return "";

    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) return "";

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  return (
    <List>
      {feedbacks?.map((feedback, index) => (
        <ListItem
          key={feedback.id}
          alignItems="flex-start"
          sx={{
            mb: 2,
            py: 1.5,
            px: 3,
            bgcolor: "#f3edfd",
            borderRadius: 2,
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <ListItemAvatar>
            <Avatar src={feedback?.avatar} sx={{ width: 48, height: 48 }}>
              {feedback?.feedback_from_empname?.charAt(0)}
            </Avatar>
          </ListItemAvatar>

          <ListItemText
            primary={
              <Box
                display="flex"
                alignItems="center"
                justifyContent={"space-between"}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {feedback?.feedback_from_empname}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    to {feedback?.feedback_to_empname}
                  </Typography>
                  <Chip
                    label={feedback?.feedback_type}
                    size="small"
                    color={getTypeColor(feedback?.feedback_type)}
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(feedback?.createdon)}
                  </Typography>
                  {/* Rating */}
                  {feedback?.feedback_rating !== undefined && (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Rating
                        value={feedback?.feedback_rating}
                        readOnly
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {feedback?.feedback_rating}/5
                      </Typography>
                    </Box>
                  )}
                </Box>
               { feedbackType !== "Received" && ( <Box display="flex" alignItems="center" gap={"12px"}>
                  <Tooltip title={"Edit"}>
                    <DriveFileRenameOutlineIcon
                      onClick={() => handleFeedbackPopup(feedback, "Edit")}
                      fontSize="small"
                      sx={{ color: "grey.600", cursor: "pointer" }}
                    />
                  </Tooltip>
                  <Tooltip title={"Delete"}>
                    <DeleteIcon
                      onClick={() => handleFeedbackPopup(feedback, "Delete")}
                      fontSize="small"
                      sx={{ color: "grey.600", cursor: "pointer" }}
                    />
                  </Tooltip>
                </Box>)}
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, wordBreak: "break-word", overflowWrap: "break-word",  }}>
                  {feedback.feedback_title}
                </Typography>
                <Typography variant="body1" 
                  sx={{ mb: 0.5,  
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-line", }}>
                  {feedback.feedback_comments}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Button
                    size="small"
                    startIcon={<Reply fontSize="small" />}
                     onClick={() =>
                      setReplyingTo(replyingTo === feedback.feedback_pk ? null : feedback.feedback_pk)
                    }
                  >
                    Reply
                  </Button>
                </Box>
                    {replyingTo === feedback.feedback_pk && (
                  <Box display="flex" gap={1} mt={1} ml={3} alignItems={"flex-end"}>
                    <TextField
                      size="small"
                      placeholder="Write a reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      fullWidth
                      multiline
                      maxRows={3}
                      sx={{
                        "& .MuiInputBase-input": {
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-line",
                        },
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => handleReplySubmit(feedback)}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {feedback?.feedback_json?.length > 0 && (
                  <Box
                    sx={{
                      ml: 3,
                      mt: 1,
                      borderRadius: 1,
                      backgroundColor: "#f4f2f7",
                      border: "1px solid #E0E0E0",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        p: 0.5,
                        bgcolor: "grey.50",
                        minHeight: "70px",
                        maxHeight: "25vh",
                        overflowY: "auto",
                      }}
                    >
                      {feedback?.feedback_json?.map((reply) => ( 
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 1,
                            px: 2,
                            py: 1,
                            // bgcolor: "#f3edfd",
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: 2,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Avatar
                            src={reply?.avatar}
                            sx={{ width: 32, height: 32 }}
                          >
                            {reply?.from.charAt(0)}
                          </Avatar>
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={1}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                {reply?.from}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {reply?.date}
                              </Typography>
                            </Box>
                            <Typography variant="body2" 
                            sx={{  
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "pre-line" }}>
                              {reply?.message}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

export const FeedbackListSkeleton = ({ count = 3 }) => {
  return (
    <List>
      {Array.from({ length: count }).map((_, index) => (
        <ListItem
          key={index}
          alignItems="flex-start"
          sx={{
            mb: 2,
            py: 1.5,
            px: 3,
            bgcolor: "#f3edfd",
            borderRadius: 2,
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <ListItemAvatar>
            <Skeleton variant="circular" width={48} height={48} />
          </ListItemAvatar>

          <Box sx={{ flex: 1 }}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Skeleton variant="text" width={80} height={24} />
              <Skeleton variant="text" width={60} height={20} />
              <Skeleton
                variant="rectangular"
                width={80}
                height={24}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton variant="text" width={60} height={20} />
            </Box>

            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
            <Skeleton
              variant="text"
              width="100%"
              height={20}
              sx={{ mb: 0.5 }}
            />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />

            <Box display="flex" alignItems="center" gap={1}>
              <Skeleton variant="rectangular" width={100} height={24} />
              <Skeleton variant="text" width={40} height={20} />
            </Box>
            <Box mt={1} ml={3}>
              {Array.from({ length: 2 }).map((_, rIndex) => (
                <Box
                  key={rIndex}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                  }}
                >
                  <Skeleton variant="circular" width={32} height={32} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width={100}
                      height={20}
                      sx={{ mb: 0.5 }}
                    />
                    <Skeleton variant="text" width="90%" height={20} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </ListItem>
      ))}
    </List>
  );
};
const feedbackDataS = [
  {
    id: 1,
    from: "Manisha Yopsx",
    to: "Raj Kumar",
    feedback_title: "Excellent Code Quality",
    feedback_type: "Public",
    visibility: "Everyone",
    feedback_rating: 5,
    feedback_comments: "Raj consistently delivers clean and efficient code. Very reliable!",
    createdon: "2025-09-22",
    avatar: "", // Optional, leave empty if no image
    replies: [
      {
        id: 101,
        from: "John Doe",
        message: "Thank you! Really appreciate the feedback.",
        timestamp: "2025-09-22",
        avatar: "",
      },
      {
        id: 102,
        from: "Sohail",
        message: "Thanks for the suggestion, I will focus on improving this.",
        timestamp: "2025-09-18",
        avatar: "",
      },

    ],
  },
  
];

export default FeedbackList;
